import type { Lead, ScoringResult, MarketComparison, FinancialProjection, Answer } from '@/types';
import type { ProductConfig } from '@/types/product';
import { ResultsSummary } from '../components/results/ResultsSummary';
import { generateDiagnosticPdf, downloadPdf } from '@/services/pdf/pdf-generator';
import { generatePdfFilename } from '@/utils/formatters';

export interface ResultScreenOptions {
  lead: Lead;
  scoring: ScoringResult;
  marketComparison: MarketComparison[];
  financialProjection: FinancialProjection | null;
  answers: Answer[];
  productConfig: ProductConfig;
  onPdfReady?: (blob: Blob) => void;
}

export class ResultScreen {
  private container: HTMLElement;
  private options: ResultScreenOptions;
  private resultsSummary: ResultsSummary | null = null;
  private pdfBlob: Blob | null = null;

  constructor(parentElement: HTMLElement, options: ResultScreenOptions) {
    this.options = options;
    this.container = document.createElement('div');
    this.container.className = 'result-screen';

    this.render();
    parentElement.appendChild(this.container);

    this.generatePdfOnLoad();
  }

  private getCompanyName(): string {
    const { productConfig, answers, lead } = this.options;
    if (productConfig.companyNameQuestionId) {
      const answer = answers.find(a => a.questionId === productConfig.companyNameQuestionId);
      if (answer && typeof answer.value === 'string' && answer.value.trim()) {
        return answer.value.trim();
      }
    }
    return lead.nome;
  }

  private render(): void {
    this.resultsSummary = new ResultsSummary(this.container, {
      companyName: this.getCompanyName(),
      scoring: this.options.scoring,
      nonScoringCategories: this.options.productConfig.scoring.nonScoringCategories,
      showDownloadButton: this.options.productConfig.id !== 'franqueabilidade',
      showContactHighlight: this.options.productConfig.id === 'franqueabilidade',
      questions: this.options.productConfig.questions,
      answers: this.options.answers,
      onDownload: () => this.handleDownload()
    });
  }

  private async generatePdfOnLoad(): Promise<void> {
    console.log('[ResultScreen] Generating PDF automatically...');

    try {
      this.pdfBlob = await generateDiagnosticPdf({
        lead: this.options.lead,
        scoring: this.options.scoring,
        marketComparison: this.options.marketComparison,
        financialProjection: this.options.financialProjection,
        answers: this.options.answers,
        productConfig: this.options.productConfig
      });

      console.log('[ResultScreen] PDF generated successfully, size:', this.pdfBlob.size);

      if (this.options.onPdfReady) {
        this.options.onPdfReady(this.pdfBlob);
      }
    } catch (error) {
      console.error('[ResultScreen] Error generating PDF on load:', error);
    }
  }

  private async handleDownload(): Promise<void> {
    if (this.pdfBlob) {
      const filename = generatePdfFilename(this.options.lead.id);
      downloadPdf(this.pdfBlob, filename);
      return;
    }

    this.resultsSummary?.setDownloading(true);

    try {
      this.pdfBlob = await generateDiagnosticPdf({
        lead: this.options.lead,
        scoring: this.options.scoring,
        marketComparison: this.options.marketComparison,
        financialProjection: this.options.financialProjection,
        answers: this.options.answers,
        productConfig: this.options.productConfig
      });

      const filename = generatePdfFilename(this.options.lead.id);
      downloadPdf(this.pdfBlob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erro ao gerar o PDF. Por favor, tente novamente.');
    } finally {
      this.resultsSummary?.setDownloading(false);
    }
  }

  remove(): void {
    this.container.remove();
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
